import { AuthApiPayload, OAuthParamsV2 } from '../../shared/types';
import { convertParams } from './convertParams';
import {
  AuthenticateError,
  DuplicateAccountError,
} from '../../shared/types/authErrors';
import {
  CreateAccountRequest,
  createAccount,
  getAuthorizeUrl,
  getAuthorizeForReconnectUrl,
  ReconnectAccountRequest,
  reconnectAccount,
  MigrationReconnectRequest,
  migrationReconnectAccount,
} from '../api/UnifiedAccountApi';

// Re-export from shared for backward compatibility
export { resolveOAuthScopeSet } from '../../shared/utils/resolveOAuthScopeSet';
export { createBuildAuthPayload } from '../../shared/utils/buildAuthPayload';

export function prepareOAuthParams(oAuthParams: OAuthParamsV2): OAuthParamsV2 {
  return {
    state: oAuthParams.state,
    ...(oAuthParams.scopeSet && { scopeSet: oAuthParams.scopeSet }),
    ...(oAuthParams.mcpServerUrl && { mcpServerUrl: oAuthParams.mcpServerUrl }),
    ...(oAuthParams.clientId && { clientId: oAuthParams.clientId }),
  };
}

export function prepareAccountRequest(
  payload: AuthApiPayload<OAuthParamsV2>,
  appId: number
): CreateAccountRequest {
  return {
    appId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams),
    ...(payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams),
    }),
    ...(payload.customParams && { customParams: payload.customParams }),
  };
}

export function prepareReconnectAccountRequest(
  payload: AuthApiPayload,
  accountId: number,
  incremental?: boolean
): ReconnectAccountRequest {
  return {
    accountId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams),
    ...(payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams as OAuthParamsV2),
    }),
    ...(incremental !== undefined && { incremental }),
    ...(payload.customParams && { customParams: payload.customParams }),
  };
}

export interface CreateAuthorizeUrlFetcherOptions {
  portalId: number;
  appId: number;
  sessionId: string;
}

export function createAuthorizeUrlFetcher({
  portalId,
  appId,
  sessionId,
}: CreateAuthorizeUrlFetcherOptions) {
  return async function makeAuthorizeUrlDescriptor(
    payload: AuthApiPayload<OAuthParamsV2>
  ): Promise<{ authorizeUrl: string }> {
    const response = await getAuthorizeUrl(portalId, {
      appId,
      sessionId,
      inputParams: convertParams(payload.inputParams),
      ...(payload.oAuthParams && {
        oauthParams: prepareOAuthParams(payload.oAuthParams),
      }),
      ...(payload.customParams && { customParams: payload.customParams }),
    });

    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(
        response.localizedErrorMessage,
        response.extraDetailsMessage
      );
    }

    return { authorizeUrl: response.authorizeUrl };
  };
}

export interface CreateReconnectAuthorizeUrlFetcherOptions {
  portalId: number;
  accountId: number;
  sessionId: string;
  incremental?: boolean;
}

export function createReconnectAuthorizeUrlFetcher({
  portalId,
  accountId,
  sessionId,
  incremental,
}: CreateReconnectAuthorizeUrlFetcherOptions) {
  return async function makeAuthorizeUrlDescriptor(
    payload: AuthApiPayload<OAuthParamsV2>
  ): Promise<{ authorizeUrl: string }> {
    const response = await getAuthorizeForReconnectUrl(portalId, {
      accountId,
      sessionId,
      inputParams: convertParams(payload.inputParams),
      ...(payload.oAuthParams && {
        oauthParams: prepareOAuthParams(payload.oAuthParams),
      }),
      ...(incremental !== undefined && { incremental }),
      ...(payload.customParams && { customParams: payload.customParams }),
    });

    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(
        response.localizedErrorMessage,
        response.extraDetailsMessage
      );
    }

    return { authorizeUrl: response.authorizeUrl };
  };
}

export interface CreateFinishNewAccountHandlerOptions {
  portalId: number;
  appId: number;
}

export function createFinishNewAccountHandler({
  portalId,
  appId,
}: CreateFinishNewAccountHandlerOptions) {
  return async function finishAuthFlow(
    payload: AuthApiPayload
  ): Promise<{ accountId: number }> {
    const request = prepareAccountRequest(payload, appId);
    const response = await createAccount(portalId, request);

    if (response['@result'] !== 'OK') {
      if (
        response.errorType === 'ACCOUNT_ALREADY_EXISTS' &&
        response.existingAccountId !== undefined
      ) {
        throw new DuplicateAccountError(response.existingAccountId);
      }
      throw new AuthenticateError(
        response.localizedErrorMessage,
        response.extraDetailsMessage
      );
    }

    return { accountId: response.accountId };
  };
}

export interface CreateFinishReconnectHandlerOptions {
  portalId: number;
  accountId: number;
  incremental?: boolean;
}

export function createFinishReconnectHandler({
  portalId,
  accountId,
  incremental,
}: CreateFinishReconnectHandlerOptions) {
  return async function finishAuthFlow(
    payload: AuthApiPayload
  ): Promise<{ accountId: number }> {
    const request = prepareReconnectAccountRequest(
      payload,
      accountId,
      incremental
    );
    const response = await reconnectAccount(portalId, request);

    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(
        response.localizedErrorMessage,
        response.extraDetailsMessage
      );
    }

    return { accountId };
  };
}

export function prepareMigrationReconnectRequest(
  payload: AuthApiPayload<OAuthParamsV2>,
  appId: number,
  oAuthorAccountId?: string
): MigrationReconnectRequest {
  return {
    appId,
    sessionId: payload.sessionId,
    inputParams: convertParams(payload.inputParams),
    upstreamParams: convertParams(payload.upstreamParams),
    ...(payload.oAuthParams && {
      oauthParams: prepareOAuthParams(payload.oAuthParams),
    }),
    ...(oAuthorAccountId && { oAuthorAccountId }),
    ...(payload.customParams && { customParams: payload.customParams }),
  };
}

export interface CreateFinishMigrationReconnectHandlerOptions {
  portalId: number;
  appId: number;
  oAuthorAccountId?: string;
}

export function createFinishMigrationReconnectHandler({
  portalId,
  appId,
  oAuthorAccountId,
}: CreateFinishMigrationReconnectHandlerOptions) {
  return async function finishAuthFlow(
    payload: AuthApiPayload
  ): Promise<{ accountId: number }> {
    const request = prepareMigrationReconnectRequest(
      payload,
      appId,
      oAuthorAccountId
    );
    const response = await migrationReconnectAccount(portalId, request);

    if (response['@result'] !== 'OK') {
      throw new AuthenticateError(
        response.localizedErrorMessage,
        response.extraDetailsMessage
      );
    }

    return { accountId: response.accountId };
  };
}
